/**
 * Sentences — the unit the Verify stage judges.
 *
 * The pipeline produces word-level clips, but judging one word at a time is
 * both slow and hard: a word out of context is often unrecognisable even to
 * the person who said it. So Verify plays the SENTENCE and highlights each
 * word as it is spoken, and a verdict covers the whole run.
 *
 * A sentence is a run of consecutive non-silence clips: silence clips (and
 * the ends of the recording) are the boundaries. Nothing is re-cut here —
 * this is purely a reading of the existing records.
 */

import { activeLeaves, type Segment } from '../types';

export interface Sentence {
  /** Stable id: the first word's id. Used as the queue key. */
  id: string;
  taskId: string;
  /** The word/filler/noise clips of this sentence, in time order. */
  words: Segment[];
  startSec: number;
  endSec: number;
  /** The sentence text, from the final label of each word. */
  text: string;
}

/** Final label of one clip: the human's correction wins over the machine's. */
export function wordText(s: Segment): string {
  return (s.correctedText ?? s.heard).trim();
}

/**
 * Group a task's leaf clips into sentences, split at silence.
 *
 * PRECONDITION: `segments` must be the task's COMPLETE leaf set. Sentences are
 * defined by where silence clips fall, so handing this a filtered queue (only
 * the pending rows, say) removes the very clips that mark the boundaries and
 * fuses sentences that should have been separate. VerifyPage currently calls it
 * with the pending subset — see the TODO there.
 */
export function groupIntoSentences(segments: Segment[]): Sentence[] {
  const leaves = activeLeaves(segments);

  const out: Sentence[] = [];
  let run: Segment[] = [];
  const flush = () => {
    if (run.length === 0) return;
    out.push({
      id: run[0].id,
      taskId: run[0].taskId,
      words: run,
      startSec: run[0].startSec,
      endSec: run[run.length - 1].endSec,
      text: run.map(wordText).join(' ').replace(/\s+/g, ' ').trim(),
    });
    run = [];
  };

  for (const s of leaves) {
    if (s.kind === 'silence') {
      flush();
      continue;
    }
    run.push(s);
  }
  flush();
  return out;
}
